import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Globe, Briefcase, FileText, CheckCircle2, Share2, ShieldCheck, Users } from 'lucide-react';
import { Link } from 'react-router-dom';

interface FloatingNodeProps {
  icon: any;
  label: string;
  color: string;
  x: number;
  y: number;
  delay: number;
}

const FloatingNode: React.FC<FloatingNodeProps> = ({ icon: Icon, label, color, x, y, delay }) => {
  const colorClasses = {
    indigo: "bg-indigo-600 text-white border-indigo-200 shadow-indigo-600/20",
    emerald: "bg-emerald-600 text-white border-emerald-200 shadow-emerald-600/20",
    amber: "bg-amber-600 text-white border-amber-200 shadow-amber-600/20",
    slate: "bg-slate-900 text-white border-slate-200 shadow-slate-900/20"
  };

  return (
    <motion.div
      className="absolute z-20 flex flex-col items-center gap-3"
      style={{ left: '50%', top: '50%', x, y }} // Base position center + offset
      initial={{ opacity: 0, scale: 0 }}
      animate={{ 
        opacity: 1, 
        scale: 1,
        y: [y, y - 8, y],
      }}
      transition={{ 
        opacity: { duration: 0.5, delay },
        scale: { duration: 0.5, delay, type: "spring" },
        y: { duration: 4, repeat: Infinity, ease: "easeInOut", delay: delay + Math.random() } // Floating effect
      }}
    >
      <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-2xl border-4 border-white ${colorClasses[color as keyof typeof colorClasses]}`}>
        <Icon size={24} />
      </div>
      <div className="px-3 py-1.5 bg-white/90 backdrop-blur-md rounded-lg border border-slate-200 shadow-sm text-[10px] font-black uppercase tracking-widest text-slate-600 whitespace-nowrap">
        {label}
      </div>
    </motion.div>
  );
};

interface ConnectionLineProps {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  dashed?: boolean;
  delay?: number;
}

const ConnectionLine: React.FC<ConnectionLineProps> = ({ x1, y1, x2, y2, dashed = false, delay = 0 }) => {
   return (
      <>
         <motion.path
            d={`M ${x1} ${y1} L ${x2} ${y2}`}
            fill="none"
            stroke={dashed ? "#cbd5e1" : "#6366f1"}
            strokeWidth={dashed ? 1.5 : 2}
            strokeDasharray={dashed ? "6 6" : "none"}
            strokeOpacity={dashed ? 0.4 : 0.15}
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: dashed ? 0.4 : 0.15 }}
            transition={{ duration: 1.5, delay }}
         />
         {!dashed && (
            <motion.circle r="3" fill="#6366f1">
               <animateMotion
                  dur="3s"
                  repeatCount="indefinite"
                  path={`M ${x1} ${y1} L ${x2} ${y2}`}
               />
            </motion.circle>
         )}
      </>
   )
}

interface FloatingBadgeProps {
  label: string;
  x: number;
  y: number;
  delay: number;
}

const FloatingBadge: React.FC<FloatingBadgeProps> = ({ label, x, y, delay }) => (
   <motion.div
      className="absolute z-10 px-3 py-1.5 bg-white border border-slate-100 rounded-full shadow-lg flex items-center gap-2"
      style={{ left: '50%', top: '50%', x, y }}
      initial={{ opacity: 0, y: y + 20 }}
      animate={{ 
         opacity: [0, 1, 1, 0],
         y: [y + 20, y, y, y - 20]
      }}
      transition={{ 
         duration: 4, 
         repeat: Infinity, 
         delay, 
         repeatDelay: 5 
      }}
   >
      <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
      <span className="text-[9px] font-bold uppercase tracking-widest text-slate-600">{label}</span>
   </motion.div>
);

const NetworkGraphic = () => {
   // Coordinates relative to 500x500 viewBox
   const center = { x: 250, y: 250 };
   const nodes = [
      { id: 1, x: 250, y: 100, label: "Statutory Audit", icon: ShieldCheck, color: "indigo" }, // Top
      { id: 2, x: 100, y: 380, label: "Global Tax", icon: Globe, color: "emerald" }, // Bottom Left
      { id: 3, x: 400, y: 380, label: "Advisory", icon: Briefcase, color: "amber" }, // Bottom Right
   ];

   return (
      <div className="relative w-full h-[500px] flex items-center justify-center select-none pointer-events-none perspective-1000">
         {/* Background Glow */}
         <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-[400px] h-[400px] bg-indigo-50/60 rounded-full blur-[80px]" />
         </div>

         {/* Orbit Rings */}
         <div className="absolute inset-0 flex items-center justify-center">
            <motion.div 
               className="w-[380px] h-[380px] border border-slate-100/60 rounded-full"
               animate={{ rotate: 360 }}
               transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
            />
            <motion.div 
               className="absolute w-[260px] h-[260px] border border-indigo-50 rounded-full"
               animate={{ rotate: -360 }}
               transition={{ duration: 45, repeat: Infinity, ease: "linear" }}
            />
         </div>

         {/* SVG Layer */}
         <svg className="absolute inset-0 w-full h-full overflow-visible z-10">
            {/* Connections from Center to Nodes */}
            {nodes.map((node, i) => (
               <ConnectionLine 
                  key={`c-${i}`} 
                  x1={center.x} y1={center.y} 
                  x2={node.x} y2={node.y} 
                  delay={1 + i * 0.2} 
               />
            ))}
            
            {/* Connections between Nodes (Triangle) */}
            <ConnectionLine x1={nodes[0].x} y1={nodes[0].y} x2={nodes[1].x} y2={nodes[1].y} dashed delay={2} />
            <ConnectionLine x1={nodes[1].x} y1={nodes[1].y} x2={nodes[2].x} y2={nodes[2].y} dashed delay={2.2} />
            <ConnectionLine x1={nodes[2].x} y1={nodes[2].y} x2={nodes[0].x} y2={nodes[0].y} dashed delay={2.4} />
         </svg>

         {/* Central Core */}
         <motion.div 
            className="absolute z-30 flex items-center justify-center"
            style={{ left: '50%', top: '50%', x: '-50%', y: '-50%' }}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", duration: 1, bounce: 0.5 }}
         >
            <div className="w-24 h-24 bg-white rounded-[2rem] shadow-2xl flex items-center justify-center border-4 border-slate-50 relative z-20">
               <div className="w-14 h-14 bg-indigo-600 rounded-xl flex items-center justify-center text-white font-display font-black text-xl shadow-inner">
                  MP
               </div>
            </div>
            {/* Pulse */}
            <motion.div 
               className="absolute inset-0 bg-indigo-500/10 rounded-[2rem] z-10"
               animate={{ scale: [1, 1.3, 1], opacity: [0.8, 0, 0.8] }}
               transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            />
         </motion.div>

         {/* Nodes */}
         {nodes.map((node, i) => (
            <FloatingNode 
               key={node.id}
               icon={node.icon}
               label={node.label}
               color={node.color}
               x={node.x - 250} // Offset from center
               y={node.y - 250} // Offset from center
               delay={0.5 + i * 0.2}
            />
         ))}

         {/* Floating Success Badges */}
         <FloatingBadge label="New Lead: Dubai" x={180} y={-80} delay={4} />
         <FloatingBadge label="Audit Signed: London" x={-200} y={40} delay={7} />
      </div>
   );
};

const Hero: React.FC = () => {
  return (
    <section className="relative min-h-[90vh] flex flex-col justify-center pt-32 pb-20 overflow-hidden bg-slate-50">
      
      {/* Background: Subtle Grid + Professional Gradient */}
      <div className="absolute inset-0 z-0 pointer-events-none">
         <div className="absolute inset-0 bg-[linear-gradient(to_right,#00000005_1px,transparent_1px),linear-gradient(to_bottom,#00000005_1px,transparent_1px)] bg-[size:32px_32px]"></div>
         <div className="absolute top-[-20%] right-[-10%] w-[600px] h-[600px] bg-indigo-200/20 blur-[120px] rounded-full" />
         <div className="absolute bottom-[-20%] left-[-10%] w-[500px] h-[500px] bg-blue-200/20 blur-[100px] rounded-full" />
         <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-slate-50 to-transparent" />
      </div>
      
      <div className="max-w-7xl mx-auto px-6 w-full relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        
        {/* Left: Community & Professional Copy */}
        <div className="flex flex-col items-start text-left">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-slate-600 text-[10px] font-bold uppercase tracking-widest mb-8 shadow-sm"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-indigo-600 animate-pulse" />
            CA Manthan Paliwal • Founder
          </motion.div>

          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-display font-extrabold text-4xl sm:text-5xl md:text-6xl text-slate-900 leading-[1.1] tracking-tight mb-6"
          >
            Where Chartered Accountants <br />
            <span className="text-indigo-600">Build Trust & Win Clients.</span>
          </motion.h1>

          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-slate-600 text-base sm:text-lg md:text-xl max-w-xl leading-relaxed mb-10 font-medium"
          >
            CAs create profiles + service pages, publish blogs/insights, collaborate, and receive <span className="text-slate-900 font-bold">global client leads</span> via the platform.
          </motion.p>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto"
          >
            <Link to="/#contact" className="px-8 py-4 bg-indigo-600 text-white font-bold rounded-xl text-sm hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-600/20 flex items-center justify-center gap-2 group">
              Join the CA Community
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/blog" className="px-8 py-4 bg-white text-slate-700 font-bold rounded-xl text-sm border border-slate-200 hover:bg-slate-50 transition-all flex items-center justify-center gap-2 shadow-sm">
              Explore Marketplace
            </Link>
          </motion.div>

          <motion.div 
             initial={{ opacity: 0 }}
             animate={{ opacity: 1 }}
             transition={{ delay: 0.5 }}
             className="mt-12 flex flex-wrap items-center gap-y-3 gap-x-6 text-slate-500"
          >
             <div className="flex items-center gap-2 text-[10px] uppercase font-black tracking-widest text-slate-500">
                <CheckCircle2 size={14} className="text-indigo-600" />
                Verified CA Profiles
             </div>
             <div className="hidden sm:block h-4 w-px bg-slate-300" />
             <div className="flex items-center gap-2 text-[10px] uppercase font-black tracking-widest text-slate-500">
                <Globe size={14} className="text-indigo-600" />
                Global Client Leads
             </div>
             <div className="hidden sm:block h-4 w-px bg-slate-300" />
             <div className="flex items-center gap-2 text-[10px] uppercase font-black tracking-widest text-slate-500">
                <Share2 size={14} className="text-indigo-600" />
                Collaboration + Referrals
             </div>
             <div className="hidden sm:block h-4 w-px bg-slate-300" />
             <div className="flex items-center gap-2 text-[10px] uppercase font-black tracking-widest text-slate-500">
                <FileText size={14} className="text-indigo-600" />
                Blogs, Templates & Insights
             </div>
          </motion.div>
        </div>

        {/* Right: Premium Network Animation */}
        <motion.div 
          className="relative lg:h-[500px] flex items-center justify-center"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1 }}
        >
          <NetworkGraphic />
        </motion.div>

      </div>
    </section>
  );
};

export default Hero;